import { WizardData } from '../context/WizardContext';
import { AdvisorInfo } from '../types/advisor';
import { formatAdvisorNameForEmail } from '../utils/formatAdvisorName';

export interface EmailTemplateData {
  wizardData: WizardData;
  advisor?: AdvisorInfo | null;
  caseId?: string | null;
  customerName: string;
  advisorLink?: string;
  customTemplateData?: Record<string, any>;
}

// Customer email templates
export const generateDefaultCustomerEmail = (data: EmailTemplateData): string => {
  const { wizardData, advisor, customerName } = data;
  const { personal } = wizardData;
  const brandName = advisor?.company || advisor?.name || 'PlanScout';
  const teamName = advisor?.company ? `The ${advisor.company} Team` : advisor?.name || 'The PlanScout Team';

  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #3f51b5;">Thank You for Your Submission!</h2>
      <p>Dear ${personal.client1.firstName},</p>
      <p>Thank you for completing your financial assessment with ${brandName}. Our team is working on your personalized financial plan and will have it ready for you in five business days.</p>
      <p>Here's what you can expect next:</p>
      <ol>
        <li>Our team will review your information</li>
        <li>We will prepare your personalized financial plan</li>
        <li>You'll receive your plan via email when it's ready with instructions on how to access your plan in our software</li>
      </ol>
      <p>If you have any questions in the meantime, feel free to reply to this email or contact our support team.</p>
      <p>Best regards,<br>${teamName}</p>
    </div>
  `;
};

export const generateThankYouEmail = (data: EmailTemplateData): string => {
  const { wizardData, advisor, customerName } = data;
  const { personal } = wizardData;
  const teamName = advisor?.company ? `The ${advisor.company} Team` : advisor?.name || 'The PlanScout Team';

  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #3f51b5;">Thank You for Your Submission!</h2>
      <p>Dear ${personal.client1.firstName},</p>
      <p>We've received your financial planning information and appreciate your trust in us.</p>
      <p>Your personalized financial plan is being prepared by our expert team and will be delivered to you shortly.</p>
      <p>If you have any immediate questions, please don't hesitate to reach out.</p>
      <p>Warm regards,<br>${teamName}</p>
    </div>
  `;
};

// Advisor email templates
export const generateStandardAdvisorEmail = (data: EmailTemplateData): string => {
  const { advisor, customerName, advisorLink } = data;
  const { personal } = data.wizardData;

  if (!advisor) return '';

  return `
    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; line-height: 1.6;">
      <div style="background-color: #3f51b5; color: white; padding: 25px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 24px; font-weight: 600; color: white;">New Client Plan Request</h1>
        <p style="margin: 8px 0 0 0; font-size: 16px; color: #e8eaf6;">PlanScout Financial Planning</p>
      </div>

      <div style="background: #ffffff; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
        <p style="font-size: 16px; margin-bottom: 20px;">Dear ${formatAdvisorNameForEmail(advisor.name)},</p>

        <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #4caf50;">
          <p style="margin: 0; font-size: 16px; font-weight: 600; color: #2e7d32;">
            📋 <strong>${customerName}</strong> just submitted a plan request using your unique PlanScout link!
          </p>
        </div>

        <div style="background-color: #f8f9fa; padding: 25px; border-radius: 8px; margin: 25px 0;">
          <h3 style="margin-top: 0; color: #3f51b5; font-size: 18px;">📈 What's happening now:</h3>
          <ul style="margin: 15px 0 0 0; padding-left: 20px; color: #333;">
            <li style="margin-bottom: 8px;">Our expert team is analyzing ${personal.client1.firstName}'s financial situation</li>
            <li style="margin-bottom: 8px;">We're creating a comprehensive, personalized retirement strategy</li>
            <li style="margin-bottom: 8px;"><strong>You'll be notified immediately</strong> when the plan is ready for your review</li>
            <li style="margin-bottom: 0;">The plan will include actionable recommendations tailored to your client's goals</li>
          </ul>
        </div>
        
        <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 25px 0; border-left: 4px solid #ffc107;">
          <h4 style="margin-top: 0; color: #856404; font-size: 16px;">⏱️ Expected Timeline:</h4>
          <p style="margin: 10px 0 0 0; color: #856404;">Your client's plan will be ready within <strong>3-5 business days</strong>. We'll send you a detailed notification with next steps.</p>
        </div>
        
        ${advisorLink ? `
        <div style="background-color: #e3f2fd; padding: 20px; border-radius: 8px; margin: 25px 0;">
          <h4 style="margin-top: 0; color: #1976d2; font-size: 16px;">🔗 Your Advisor Link:</h4>
          <div style="background: white; padding: 12px; border-radius: 4px; border: 1px solid #bbdefb; margin-top: 10px;">
            <a href="${advisorLink}" style="color: #1976d2; text-decoration: none; font-weight: 600; word-break: break-all;">${advisorLink}</a>
          </div>
          <p style="margin: 10px 0 0 0; font-size: 14px; color: #666;">Share this link with future clients for seamless plan requests</p>
        </div>
        ` : ''}
        
        <div style="border-top: 2px solid #f0f0f0; padding-top: 20px; margin-top: 30px;">
          <p style="font-size: 16px; margin-bottom: 15px;">Questions? Need immediate assistance?</p>
          <p style="margin-bottom: 5px;">📧 Email: <a href="mailto:service@planscout.com" style="color: #1976d2;">service@planscout.com</a></p>
          <p style="margin-bottom: 20px;">📞 Phone: <a href="tel:2064663766" style="color: #1976d2;">(206)466-3766</a></p>
        </div>
        
        <p style="font-size: 16px; margin-bottom: 5px;">Best regards,</p>
        <p style="font-weight: 600; color: #3f51b5; margin-bottom: 0;">The PlanScout Team</p>
      </div>
      
      <div style="text-align: center; padding: 20px; color: #666; font-size: 13px;">
        <p style="margin: 0;">This notification was sent because ${customerName} used your personalized advisor link.</p>
        <p style="margin: 5px 0 0 0;">© ${new Date().getFullYear()} PlanScout</p>
      </div>
    </div>
  `;
};

export const generateEnhancedAdvisorEmail = (data: EmailTemplateData): string => {
  // Start with standard template
  let email = generateStandardAdvisorEmail(data);
  
  // Add automation status if configured
  if (data.customTemplateData?.showAutomationStatus) {
    const automationSection = `
      <div style="background-color: #e8f5e9; padding: 20px; border-radius: 8px; margin: 25px 0; border-left: 4px solid #4caf50;">
        <h4 style="margin-top: 0; color: #2e7d32; font-size: 16px;">🤖 Automation Status:</h4>
        <p style="margin: 10px 0 0 0; color: #2e7d32;">This case has been submitted with <strong>automated processing enabled</strong>. Reports will be generated automatically and delivered to the configured recipients.</p>
      </div>
    `;
    
    // Insert automation section after the "What's happening now" section
    email = email.replace('</div>\n        \n        <div style="background-color: #fff3cd;', `</div>\n        \n        ${automationSection}\n        \n        <div style="background-color: #fff3cd;`);
  }
  
  return email;
};

// Email template registry
export const EmailTemplates = {
  customer: {
    default: generateDefaultCustomerEmail,
    thankYou: generateThankYouEmail,
    custom: (data: EmailTemplateData) => data.customTemplateData?.template || generateDefaultCustomerEmail(data)
  },
  advisor: {
    standard: generateStandardAdvisorEmail,
    enhanced: generateEnhancedAdvisorEmail,
    custom: (data: EmailTemplateData) => data.customTemplateData?.template || generateStandardAdvisorEmail(data)
  }
};

// Helper to get the appropriate template
export const getEmailTemplate = (
  type: 'customer' | 'advisor',
  templateName?: string
): (data: EmailTemplateData) => string => {
  if (type === 'customer') {
    const template = templateName as keyof typeof EmailTemplates.customer;
    return EmailTemplates.customer[template] || EmailTemplates.customer.default;
  } else {
    const template = templateName as keyof typeof EmailTemplates.advisor;
    return EmailTemplates.advisor[template] || EmailTemplates.advisor.standard;
  }
};